import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import { useEscalationContext } from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { AiChatContext } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { ToolComponentProps } from "@redotech/redo-web/ai/chat/types/tool-component-props";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useContext } from "react";
import * as styles from "./escalate-to-human-tool.module.css";
import { useInChatEscalation } from "./use-in-chat-escalation";

interface EscalationCardProps {
  title: string;
  description: string;
  error: string | null;
  children: ReactNode;
}

const EscalationCard = memo(function EscalationCard({
  title,
  description,
  error,
  children,
}: EscalationCardProps) {
  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      m="lg"
      p="lg"
      radius="md"
      shadow="lg"
    >
      <Flex align="center" dir="column" gap="sm">
        <div className={styles.iconWrapper}>
          <RedoIcon color="brand" Icon={MessageChatCircleIcon} size={20} />
        </div>

        <Text fontSize="md" fontWeight="semibold" textAlign="center">
          {title}
        </Text>

        <Text color="secondary" fontSize="sm" textAlign="center">
          {description}
        </Text>
      </Flex>

      {error && (
        <Text color="error" fontSize="xs" textAlign="center">
          {error}
        </Text>
      )}

      {children}
    </Flex>
  );
});

interface InvestigatingCardProps {
  isDone: boolean;
  error?: string | null;
  onEscalateToHumanDirectly?: () => void;
  isEscalatingToHumanDirectly?: boolean;
}

const InvestigatingCard = memo(function InvestigatingCard({
  isDone,
  error,
  onEscalateToHumanDirectly,
  isEscalatingToHumanDirectly,
}: InvestigatingCardProps) {
  const isError = !!error;

  let title: string;
  let description: string;
  if (isError) {
    title = "We couldn't complete the investigation";
    description = "Please connect with a specialist to finish helping you.";
  } else if (isDone) {
    title = "Investigation complete";
    description = "Our team has responded below.";
  } else {
    title = "Investigating";
    description = "Our team is looking into this.";
  }

  return (
    <Flex
      align="center"
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      m="lg"
      p="lg"
      radius="md"
      shadow="lg"
    >
      <Flex align="center" dir="column" gap="sm">
        <div
          className={
            isDone || isError ? styles.iconWrapper : styles.investigatingIcon
          }
        >
          <RedoIcon
            color={isDone || isError ? "brand" : "success"}
            Icon={SearchIcon}
            size={20}
          />
        </div>

        <Flex align="center" gap="xs">
          {!isDone && !isError && <RedoSpinner size="xs" />}
          <Text fontSize="md" fontWeight="semibold" textAlign="center">
            {title}
          </Text>
        </Flex>

        <Text color="secondary" fontSize="sm" textAlign="center">
          {description}
        </Text>
      </Flex>

      {isError && onEscalateToHumanDirectly && (
        <RedoButton
          className={styles.button}
          disabled={isEscalatingToHumanDirectly}
          hierarchy="primary"
          onClick={onEscalateToHumanDirectly}
          pending={isEscalatingToHumanDirectly}
          size="sm"
          text={
            isEscalatingToHumanDirectly
              ? "Connecting..."
              : "Connect me with a specialist"
          }
        />
      )}
    </Flex>
  );
});

export function EscalateToHumanTool(props: ToolComponentProps) {
  const { sendToolResponse, messages } = useContext(AiChatContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { triageThreadId, navigateToThread, escalation } =
    useEscalationContext();

  const activeEscalationLoad = useLoad(
    async (signal) => {
      const result = await merchantRpcClient.checkActiveEscalation(
        {},
        { signal },
      );
      return result.activeEscalation;
    },
    [merchantRpcClient],
  );

  const activeEscalation = activeEscalationLoad.value;
  const hasExistingEscalationOnDifferentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId !== triageThreadId;
  const hasExistingEscalationOnCurrentThread =
    activeEscalation != null &&
    activeEscalation.triageThreadId === triageThreadId;

  const sendEscalationResponse = useHandler(() => {
    void sendToolResponse(
      {
        toolCallId: props.toolCallId,
        toolName: props.toolName,
        result: { output: "Investigation started" },
      },
      { skipLlmCall: true },
    );
  });

  const {
    escalate,
    escalateToHumanDirectly,
    isLoading,
    error,
    isEscalated,
    isInvestigationDone,
    investigationStarted,
    shouldSkipCodeSearch,
  } = useInChatEscalation({
    messages,
    input: props.input,
    onSuccess: sendEscalationResponse,
  });

  const handleSameIssue = useHandler(() => {
    if (activeEscalation && navigateToThread) {
      navigateToThread(activeEscalation.triageThreadId);
      void sendToolResponse(
        {
          toolCallId: props.toolCallId,
          toolName: props.toolName,
          result: { output: "Navigated to existing escalation" },
        },
        { skipLlmCall: true },
      );
    }
  });

  // Don't render anything until the active escalation check completes.
  if (activeEscalationLoad.pending) {
    return null;
  }

  // Show the investigating card from the moment the user kicks off a real
  // code search, all the way through completion. Using `investigationStarted`
  // (set when the investigation kicks off and survives the `codeSearchAttempted`
  // flip) means the card transitions to the "done" state instead of unmounting.
  // In the skip path, the EscalationStatus banner handles the "waiting for
  // team" state, so we don't render here.
  if (investigationStarted) {
    return (
      <InvestigatingCard
        error={error}
        isDone={isInvestigationDone}
        isEscalatingToHumanDirectly={isLoading}
        onEscalateToHumanDirectly={escalateToHumanDirectly}
      />
    );
  }

  // Code search already ran — offer direct connection to team.
  // This must come before the status/disabled checks so the card is reachable
  // when a second escalate-to-human tool call renders after code search completed.
  if (
    shouldSkipCodeSearch &&
    !isEscalated &&
    props.output === undefined &&
    !props.disabled
  ) {
    return (
      <EscalationCard
        description="A team member will join the conversation to help."
        error={error}
        title="Would you like to connect with our team?"
      >
        <RedoButton
          className={styles.button}
          disabled={isLoading || props.disabled}
          hierarchy="primary"
          onClick={escalate}
          pending={isLoading}
          size="sm"
          text={isLoading ? "Connecting..." : "Yes, connect me"}
        />
      </EscalationCard>
    );
  }

  // Show completed state when loading a thread with a finished tool result
  if (props.output !== undefined) {
    const output = props.output as { type?: string } | string | undefined;
    // Synthetic code search result — show investigation complete card
    if (typeof output === "object" && output?.type === "text") {
      return <InvestigatingCard isDone />;
    }
    return null;
  }

  if (
    props.disabled ||
    escalation.status !== "idle" ||
    hasExistingEscalationOnCurrentThread
  ) {
    return null;
  }

  // Two-button UI when there's an existing active escalation on a different thread
  if (hasExistingEscalationOnDifferentThread) {
    return (
      <EscalationCard
        description="Would you like to continue your existing conversation?"
        error={error}
        title="You already have an open conversation with the team"
      >
        <Flex gap="md">
          <RedoButton
            className={styles.button}
            disabled={props.disabled || !navigateToThread}
            hierarchy="primary"
            onClick={handleSameIssue}
            size="sm"
            text="Yes"
          />
          <RedoButton
            className={styles.button}
            disabled={isLoading || props.disabled}
            hierarchy="secondary"
            onClick={escalate}
            pending={isLoading}
            size="sm"
            text={isLoading ? "Investigating..." : "No"}
          />
        </Flex>
      </EscalationCard>
    );
  }

  return (
    <EscalationCard
      description="Our team will review this and get back to you shortly."
      error={error}
      title="Would you like us to look into this?"
    >
      <RedoButton
        className={styles.button}
        disabled={isLoading || props.disabled}
        hierarchy="primary"
        onClick={escalate}
        pending={isLoading}
        size="sm"
        text={isLoading ? "Looking into it..." : "Yes, look into it"}
      />
    </EscalationCard>
  );
}
